<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import ProportionBar from '../analytics/ProportionBar.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { QUEM_CORES } from '../atendimento/quemCores';
import { campanhasKeys, campanhasLabel } from './campanhasLabels';

// Quem atendeu depois da resposta: a primeira saída que não é disparo decide —
// a IA, uma pessoa, outro robô, ou ninguém (esperando há menos de 24 h, ou
// ninguém mesmo). Os cinco somam 100% das respostas, e o servidor já manda os
// percentuais que somam 100. Da IA, quantas ela passou para a equipe, e o que
// houve depois da passagem.
const props = defineProps({
  // `after_reply` de GET campanhas/resumo; null enquanto não chegou.
  after: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number, percent } = useAnalyticsFormat();

const FILLS = {
  ai: QUEM_CORES.ia.fill,
  person: QUEM_CORES.pessoa.fill,
  other_bot: 'fill-n-slate-11',
  waiting: QUEM_CORES.espera.fill,
  nobody: 'fill-n-slate-6',
};
const DOTS = {
  ai: QUEM_CORES.ia.bg,
  person: QUEM_CORES.pessoa.bg,
  other_bot: 'bg-n-slate-11',
  waiting: QUEM_CORES.espera.bg,
  nobody: 'bg-n-slate-6',
};

const buckets = computed(() =>
  campanhasKeys('after').map(key => ({
    key,
    label: campanhasLabel(t, 'after', key),
    count: props.after?.[key]?.count ?? 0,
    percent: props.after?.[key]?.percent ?? null,
    fill: FILLS[key],
    dot: DOTS[key],
  }))
);

const replied = computed(() =>
  buckets.value.reduce((sum, bucket) => sum + bucket.count, 0)
);

const ai = computed(() => props.after?.ai || null);
const afterHandoff = computed(() =>
  campanhasKeys('handoff')
    .map(key => ({
      key,
      label: campanhasLabel(t, 'handoff', key),
      count: ai.value?.after_handoff?.[key] ?? 0,
    }))
    .filter(item => item.count > 0)
);

// "3 passaram para a equipe · Resolveram: 2 · Ainda com a equipe: 1".
const handoffLine = computed(() =>
  ai.value?.handed_off
    ? [
        t(
          'CAMPANHAS.AFTER.HANDED_OFF',
          { count: number(ai.value.handed_off) },
          ai.value.handed_off
        ),
        ...afterHandoff.value.map(
          item => `${item.label}: ${number(item.count)}`
        ),
      ].join(' · ')
    : ''
);

const reason = computed(() =>
  props.after?.reason ? campanhasLabel(t, 'reason', props.after.reason) : ''
);
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanha-depois"
    :title="t('CAMPANHAS.AFTER.TITLE')"
    :description="t('CAMPANHAS.AFTER.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <MetricValue
      v-else-if="reason"
      data-test-id="campanha-depois-sem-dado"
      :reason="reason"
    />
    <div v-else-if="after" class="flex flex-col gap-3 min-w-0">
      <ProportionBar
        class="h-2"
        :segments="
          buckets.map(bucket => ({
            key: bucket.key,
            value: bucket.count,
            fill: bucket.fill,
          }))
        "
        :total="replied"
      />
      <ul class="flex flex-col m-0 list-none">
        <li
          v-for="bucket in buckets"
          :key="bucket.key"
          :data-test-id="`campanha-depois-${bucket.key}`"
          class="flex flex-col py-1.5 min-w-0"
        >
          <span class="flex gap-3 justify-between items-baseline text-sm">
            <span class="flex gap-1.5 items-center min-w-0 text-n-slate-12">
              <span
                class="flex-shrink-0 rounded-full size-2"
                :class="bucket.dot"
              />
              {{ bucket.label }}
            </span>
            <span class="whitespace-nowrap tabular-nums">
              <span class="font-medium text-n-slate-12">
                {{ number(bucket.count) }}
              </span>
              <span
                v-if="typeof bucket.percent === 'number'"
                class="ms-1.5 text-xs text-n-slate-11"
              >
                {{ percent(bucket.percent) }}
              </span>
            </span>
          </span>
          <span
            v-if="bucket.key === 'ai' && handoffLine"
            data-test-id="campanha-depois-passagem"
            class="text-xs text-n-slate-10 ps-3.5"
          >
            {{ handoffLine }}
          </span>
        </li>
      </ul>
    </div>
  </AnalyticsPanel>
</template>
